import React, { FormEvent, useState, useEffect, useCallback } from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faPlus, faTrash, faCopy, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import useUser from '../../../hooks/useUser';
import { getErrorMessage } from '../../../utils/errorMessage';
import fetchWithTimeout from '../../../utils/fetchWithTimeout';
import AccountSettingsLoadingState from '../AccountSettingsLoadingState';
import {
  getAccountSettingsUserCacheKey,
  makeAccountSettingsCacheKey,
  readAccountSettingsCache,
  readThroughAccountSettingsCache,
} from '../accountSettingsDataCache';
import './ApiKeySettings.css';

interface ApiKeyRecord {
  id: string;
  name: string;
  keyPrefix: string;
  createdAt?: string | null;
  lastUsedAt?: string | null;
}

interface CreatedApiKey {
  key: string;
}

interface ApiKeyListResponse {
  apiKeys?: ApiKeyRecord[];
}

interface ApiKeyCreateResponse {
  apiKey: CreatedApiKey;
  message?: string;
}

const ApiKeySettings = () => {
  const { getApiToken, user } = useUser();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://localhost:8082';
  const requestTimeoutMs = 10000;
  const apiKeysCacheKey = makeAccountSettingsCacheKey(
    'api-keys',
    getAccountSettingsUserCacheKey(user),
    apiBaseUrl,
  );
  const cachedApiKeys = readAccountSettingsCache<ApiKeyRecord[]>(apiKeysCacheKey);
  const [apiKeys, setApiKeys] = useState<ApiKeyRecord[]>(() => cachedApiKeys || []);
  const [loading, setLoading] = useState(() => !cachedApiKeys);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<CreatedApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const fetchKeys = useCallback(async (options: { force?: boolean } = {}) => {
    const cached = readAccountSettingsCache<ApiKeyRecord[]>(apiKeysCacheKey);
    if (cached && !options.force) {
      setApiKeys(cached);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const nextApiKeys = await readThroughAccountSettingsCache<ApiKeyRecord[]>(apiKeysCacheKey, async () => {
        const token = await getApiToken();
        const response = await fetchWithTimeout(`${apiBaseUrl}/mcp-api-keys`, {
          headers: { Authorization: `Bearer ${token}` },
        }, requestTimeoutMs);
        if (!response.ok) {
          throw new Error('Failed to fetch API keys');
        }
        const data = await response.json() as ApiKeyListResponse;
        return data.apiKeys || [];
      }, options);
      setApiKeys(nextApiKeys);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch API keys'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, apiKeysCacheKey, getApiToken]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!newKeyName.trim()) return;

    try {
      setCreating(true);
      setError(null);
      const token = await getApiToken();
      const response = await fetchWithTimeout(`${apiBaseUrl}/mcp-api-keys`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newKeyName.trim() }),
      }, requestTimeoutMs);

      if (!response.ok) {
        const data = await response.json().catch(() => ({})) as Partial<ApiKeyCreateResponse>;
        throw new Error(data.message || 'Failed to create API key');
      }

      const data = await response.json() as ApiKeyCreateResponse;
      setNewlyCreatedKey(data.apiKey);
      setNewKeyName('');
      setShowCreateForm(false);
      await fetchKeys({ force: true });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create API key'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId: string): Promise<void> => {
    try {
      setRevokingId(keyId);
      setError(null);
      const token = await getApiToken();
      const response = await fetchWithTimeout(`${apiBaseUrl}/mcp-api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }, requestTimeoutMs);

      if (!response.ok) {
        const data = await response.json().catch(() => ({})) as { message?: string };
        throw new Error(data.message || 'Failed to revoke API key');
      }

      await fetchKeys({ force: true });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to revoke API key'));
    } finally {
      setRevokingId(null);
    }
  };

  const handleCopyKey = async (key: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = key;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="api-key-settings">
      <div className="settings-subpage-header api-key-settings-header">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title">API Keys</h2>
          <p className="settings-subpage-description">
            Create and revoke MCP keys.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="api-key-settings-alert">
          {error}
        </Alert>
      )}

      <div className="api-key-settings-controls">
        {newlyCreatedKey && (
          <Alert variant="success" className="api-key-settings-new-key-alert">
            <div className="api-key-settings-new-key-header">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <strong>Save your API key now. You won't be able to see it again.</strong>
            </div>
            <div className="api-key-settings-new-key-value">
              <code className="api-key-settings-key-code">{newlyCreatedKey.key}</code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopyKey(newlyCreatedKey.key)}
                className="api-key-settings-copy-btn"
              >
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                {copied ? ' Copied' : ' Copy'}
              </Button>
            </div>
            <div className="api-key-settings-usage-hint">
              <strong>Quick setup for Claude Code:</strong>
              <code className="api-key-settings-usage-code">
                claude mcp add --transport http botspot https://mcp.botspot.trade/mcp -H "Authorization: Bearer {newlyCreatedKey.key}"
              </code>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setNewlyCreatedKey(null)}
              className="api-key-settings-dismiss-btn"
            >
              I've saved my key
            </Button>
          </Alert>
        )}

        {!showCreateForm ? (
          <Button
            variant="primary"
            onClick={() => setShowCreateForm(true)}
            className="api-key-settings-create-btn"
            disabled={apiKeys.length >= 5}
          >
            <FontAwesomeIcon icon={faPlus} /> Create New API Key
          </Button>
        ) : (
          <section className="api-key-settings-create-form">
            <Form onSubmit={handleCreate}>
              <Form.Group className="api-key-settings-form-group">
                <Form.Label>Key Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Claude Code, Cursor IDE, My Laptop"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  maxLength={100}
                  autoFocus
                  className="api-key-settings-input"
                />
                <Form.Text className="api-key-settings-form-hint">
                  A label to help you identify this key later.
                </Form.Text>
              </Form.Group>
              <div className="api-key-settings-form-actions">
                <Button type="submit" variant="primary" disabled={creating || !newKeyName.trim()}>
                  {creating ? <><Spinner size="sm" /> Creating...</> : 'Create Key'}
                </Button>
                <Button variant="outline" onClick={() => { setShowCreateForm(false); setNewKeyName(''); }}>
                  Cancel
                </Button>
              </div>
            </Form>
          </section>
        )}

        {apiKeys.length >= 5 && !showCreateForm && (
          <p className="api-key-settings-limit-text">
            Maximum of 5 API keys reached. Revoke an existing key to create a new one.
          </p>
        )}
      </div>

      <div className="api-key-settings-list">
        {loading ? (
          <AccountSettingsLoadingState
            className="api-key-settings-loading"
            label="Loading API keys"
            text="Loading API keys..."
          />
        ) : error ? null : apiKeys.length === 0 ? (
          <div className="api-key-settings-empty">
            <FontAwesomeIcon icon={faKey} className="api-key-settings-empty-icon" />
            <p>No API keys yet. Create one to connect your MCP client.</p>
          </div>
        ) : (
          apiKeys.map((key) => (
            <article key={key.id} className="api-key-settings-key-row">
              <div className="api-key-settings-key-info">
                <div className="api-key-settings-key-name">
                  <FontAwesomeIcon icon={faKey} className="api-key-settings-key-icon" />
                  <strong>{key.name}</strong>
                </div>
                <div className="api-key-settings-key-meta">
                  <code className="api-key-settings-key-prefix">{key.keyPrefix}...</code>
                  <span className="api-key-settings-key-meta-item">
                    Created {formatDate(key.createdAt)}
                  </span>
                  {key.lastUsedAt && (
                    <span className="api-key-settings-key-meta-item">
                      Last used {formatDate(key.lastUsedAt)}
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleRevoke(key.id)}
                disabled={revokingId === key.id}
                className="api-key-settings-revoke-btn"
              >
                {revokingId === key.id ? (
                  <Spinner size="sm" />
                ) : (
                  <><FontAwesomeIcon icon={faTrash} /> Revoke</>
                )}
              </Button>
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default ApiKeySettings;
